import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ShippingOrder {
  id: string;
  order_number: string;
  status: string;
  shipping_method?: string;
  ship_to_company?: string;
  ship_to_street?: string;
  ship_to_city?: string;
  ship_to_state?: string;
  ship_to_zip?: string;
  our_cost?: number;
  dealer_cost?: number;
  freight_paid: boolean;
  total_amount?: number;
  payment_amount?: number;
  ach_discount_amount?: number;
  pending_ach_amount?: number;
  dealer_company?: string;
  dealer_first_name?: string;
  dealer_last_name?: string;
  unit_count?: number;
  created_at: string;
}

async function apiRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
}

type TabType = 'pending_freight' | 'ready_to_ship' | 'shipped';

function TabButton({ active, onClick, children, count }: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2',
        active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      )}
    >
      {children}
      {count !== undefined && (
        <span className={clsx(
          'px-2 py-0.5 rounded-full text-xs',
          active ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
        )}>
          {count}
        </span>
      )}
    </button>
  );
}

export default function ShippingPage() {
  const [activeTab, setActiveTab] = useState<TabType>('pending_freight');
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [counts, setCounts] = useState({ pending_freight: 0, ready_to_ship: 0, shipped: 0 });
  const [freightInput, setFreightInput] = useState<Record<string, string>>({});
  const [noMarkupInput, setNoMarkupInput] = useState<Record<string, boolean>>({});
  const [costInput, setCostInput] = useState<Record<string, string>>({});
  const [urgentInput, setUrgentInput] = useState<Record<string, boolean>>({});
  const [remindedIds, setRemindedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadOrders();
    loadCounts();
  }, [activeTab]);

  // The Ready-to-Ship backend queue defaults to SCREENS (it excludes
  // order_type='parts', which has its own ?order_type=parts view in the Shipping
  // Portal). Factory Admin has no parts sub-tab, so a paid parts FREIGHT order
  // (e.g. VT26100035) vanished here after "mark freight paid" — it left Pending
  // Freight but the default Ready query hid it. Fetch BOTH and merge so parts
  // stay visible alongside screens, matching how screens behave.
  const loadReadyQueue = async (): Promise<any[]> => {
    const [screens, parts] = await Promise.all([
      apiRequest('/shipping/queue/ready').catch(() => []),
      apiRequest('/shipping/queue/ready?order_type=parts').catch(() => []),
    ]);
    const merged = [...(Array.isArray(screens) ? screens : []), ...(Array.isArray(parts) ? parts : [])];
    const byId = new Map<string, any>();
    for (const o of merged) if (o && o.id && !byId.has(o.id)) byId.set(o.id, o);
    return [...byId.values()];
  };

  const loadCounts = async () => {
    try {
      // Load counts for all tabs
      const [pending, ready, shipped] = await Promise.all([
        apiRequest('/shipping/queue/pending-freight').catch(() => []),
        loadReadyQueue(),
        apiRequest('/shipping/queue/shipped?limit=50').catch(() => []),
      ]);
      setCounts({
        pending_freight: Array.isArray(pending) ? pending.length : 0,
        ready_to_ship: ready.length,
        shipped: Array.isArray(shipped) ? shipped.length : 0,
      });
    } catch (err) {
      console.error('Failed to load counts:', err);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      if (activeTab === 'ready_to_ship') {
        setOrders(await loadReadyQueue());
        return;
      }
      const endpoint = activeTab === 'shipped'
        ? '/shipping/queue/shipped'
        : '/shipping/queue/pending-freight';
      const data = await apiRequest(endpoint);
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePayFreight = async (orderId: string) => {
    if (!window.confirm('Mark freight as paid? This will move the order to the Shipping Portal queue.')) return;
    setActionLoading(orderId);
    try {
      await apiRequest('/shipping/pay-freight', {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId }),
      });
      await loadOrders();
      await loadCounts();
    } catch (err: any) {
      alert(err.message || 'Failed to pay freight');
    } finally {
      setActionLoading(null);
    }
  };

  // Mark an order shipped from the Ready-to-Ship tab. The server resyncs
  // FreightView to grab the latest tracking / PRO / BOL (carriers often give
  // only a PRO or BOL, no tracking number) and notifies the dealer.
  const handleMarkShipped = async (orderId: string, bypassScanOut = false) => {
    const msg = bypassScanOut
      ? 'OVERRIDE: ship without scan-out verification? This is logged against your account.'
      : 'Mark this order as shipped? This resyncs FreightView to pull the latest tracking / PRO / BOL and notifies the dealer.';
    if (!window.confirm(msg)) return;
    setActionLoading(orderId);
    try {
      await apiRequest('/shipping/mark-shipped', {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId, bypass_scan_out: bypassScanOut }),
      });
      await loadOrders();
      await loadCounts();
    } catch (err: any) {
      const m = err?.message || 'Failed to mark shipped';
      // If it's blocked only by the scan-out check, offer an admin override.
      if (!bypassScanOut && /scan.?out/i.test(m)) {
        setActionLoading(null);
        if (window.confirm(`${m}\n\nAdmin override — ship anyway without scan-out? (this is logged)`)) {
          return handleMarkShipped(orderId, true);
        }
        return;
      }
      alert(m);
    } finally {
      setActionLoading(null);
    }
  };

  // Enter the actual booked carrier cost → server recomputes markup + freight
  // total + order total + invoice + balance (one source of truth).
  const handleSetActualFreight = async (orderId: string) => {
    const raw = freightInput[orderId];
    const cost = parseFloat(String(raw));
    const noMarkup = !!noMarkupInput[orderId];
    if (!(cost >= 0)) { alert('Enter a valid freight cost.'); return; }
    const confirmMsg = noMarkup
      ? `Charge the dealer $${cost.toFixed(2)} for freight AT COST (no markup)? This updates the order total and invoice.`
      : `Set actual freight to $${cost.toFixed(2)}? This recomputes the dealer's freight charge (+ markup), the order total, and the invoice.`;
    if (!window.confirm(confirmMsg)) return;
    setActionLoading(orderId);
    try {
      const res: any = await apiRequest(`/orders/${orderId}/actual-freight`, {
        method: 'POST',
        body: JSON.stringify({ actualCost: cost, noMarkup }),
      });
      setFreightInput(prev => ({ ...prev, [orderId]: '' }));
      setNoMarkupInput(prev => ({ ...prev, [orderId]: false }));
      await loadOrders();
      await loadCounts();
      alert(`Freight updated: dealer charge $${(res?.freightTotal ?? 0).toFixed ? res.freightTotal.toFixed(2) : res?.freightTotal} · order total $${res?.orderTotal} · ${res?.paidInFull ? 'paid in full' : 'balance due $' + (res?.balanceDue ?? '')}`);
    } catch (err: any) {
      alert(err.message || 'Failed to set actual freight');
    } finally {
      setActionLoading(null);
    }
  };

  // Record the actual carrier COST (COGS) only — does NOT change the dealer
  // charge/invoice. Use when the carrier billed a different amount than the
  // dealer paid and we absorb the difference.
  const handleSetFreightCost = async (orderId: string) => {
    const cost = parseFloat(String(costInput[orderId]));
    if (!(cost >= 0)) { alert('Enter a valid freight cost.'); return; }
    if (!window.confirm(`Record $${cost.toFixed(2)} as our freight COST (COGS)? This does NOT change what the dealer is charged.`)) return;
    setActionLoading(orderId);
    try {
      await apiRequest(`/orders/${orderId}/freight-cost`, {
        method: 'POST',
        body: JSON.stringify({ cost }),
      });
      setCostInput(prev => ({ ...prev, [orderId]: '' }));
      await loadOrders();
      alert(`Freight cost recorded: $${cost.toFixed(2)} (dealer charge unchanged).`);
    } catch (err: any) {
      alert(err.message || 'Failed to record freight cost');
    } finally {
      setActionLoading(null);
    }
  };

  // Outstanding balance for an order (ACH discount counts as paid), rounded to cents.
  const balanceOf = (order: ShippingOrder) => {
    const total = parseFloat(String(order.total_amount || 0));
    const paid = parseFloat(String(order.payment_amount || 0));
    const achDiscount = parseFloat(String((order as any).ach_discount_amount || 0));
    return Math.round((total - paid - achDiscount) * 100) / 100;
  };

  // Email the dealer a friendly "pay your balance so we can book shipping"
  // reminder. `urgent` adds the "make this week's truck" cutoff language.
  const handleSendReminder = async (orderId: string) => {
    const urgent = !!urgentInput[orderId];
    const msg = urgent
      ? 'Send an URGENT balance reminder? The dealer is told they must pay TODAY to make this week\'s freight pickup.'
      : 'Send a friendly balance-due reminder email to this dealer?';
    if (!window.confirm(msg)) return;
    setActionLoading(orderId);
    try {
      const res: any = await apiRequest(`/orders/${orderId}/send-balance-reminder`, {
        method: 'POST',
        body: JSON.stringify({ urgent }),
      });
      setRemindedIds(prev => ({ ...prev, [orderId]: true }));
      alert(res?.message || 'Reminder sent.');
    } catch (err: any) {
      alert(err.message || 'Failed to send reminder');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredOrders = orders.filter(order =>
    order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.dealer_company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.ship_to_company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <div>
      <PageHeader
        title="Shipping Management"
        subtitle="Manage freight payments and shipping queues"
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <TabButton
          active={activeTab === 'pending_freight'}
          onClick={() => setActiveTab('pending_freight')}
          count={counts.pending_freight}
        >
          Pending Freight Payment
        </TabButton>
        <TabButton
          active={activeTab === 'ready_to_ship'}
          onClick={() => setActiveTab('ready_to_ship')}
          count={counts.ready_to_ship}
        >
          Ready to Ship
        </TabButton>
        <TabButton
          active={activeTab === 'shipped'}
          onClick={() => setActiveTab('shipped')}
          count={counts.shipped}
        >
          Recently Shipped
        </TabButton>
      </div>

      {/* Info Banner */}
      {activeTab === 'pending_freight' && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium">Orders Awaiting Freight Payment</p>
              <p className="text-yellow-700 text-sm mt-1">
                These orders are ready to ship but freight has not been paid. Click "Pay Freight" to move them to the Shipping Portal queue.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'ready_to_ship' && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-blue-800 font-medium">Ready to Ship</p>
              <p className="text-blue-700 text-sm mt-1">
                These orders have freight paid and are visible in the Shipping Portal. Shipping team will process them there.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <Card className="mb-6">
        <input
          type="text"
          placeholder="Search orders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </Card>

      {/* Orders Table */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="text-lg font-medium">No orders found</p>
            <p className="text-sm mt-1">
              {activeTab === 'pending_freight'
                ? 'No orders are awaiting freight payment'
                : activeTab === 'ready_to_ship'
                ? 'No orders are ready to ship'
                : 'No shipped orders found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ship To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Our Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer Charged</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Freight</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {order.order_number}
                      </Link>
                      {order.sidemark && (
                        <div className="text-xs text-gray-700 font-medium mt-0.5">{order.sidemark}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-0.5">
                        {order.unit_count || 0} units
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.dealer_company || '-'}</div>
                      <div className="text-xs text-gray-500">
                        {order.dealer_first_name} {order.dealer_last_name}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {order.ship_to_street && (
                        <div className="text-sm text-gray-900">{order.ship_to_street}</div>
                      )}
                      <div className="text-xs text-gray-500">
                        {order.ship_to_city}{order.ship_to_state ? `, ${order.ship_to_state}` : ''} {order.ship_to_zip}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {order.shipping_method === 'will_call' ? (
                        <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs">Will Call</span>
                      ) : (
                        <span className="capitalize">{order.shipping_method === 'shipping' ? 'Freight' : (order.shipping_method || 'Freight')}</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {order.shipping_method === 'will_call' ? '-' : formatCurrency(order.our_cost)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {order.shipping_method === 'will_call' ? '-' : formatCurrency(order.dealer_cost)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {order.shipping_method === 'will_call' ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">N/A</span>
                      ) : order.freight_paid ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Freight Paid</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(() => {
                        const total = parseFloat(String(order.total_amount || 0));
                        const paid = parseFloat(String(order.payment_amount || 0));
                        const achDiscount = parseFloat(String(order.ach_discount_amount || 0));
                        const balance = Math.round((total - paid - achDiscount) * 100) / 100;
                        if (total <= 0) return <span className="text-xs text-gray-400">-</span>;
                        if (balance <= 0) return (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Paid in Full</span>
                        );
                        return (
                          <div>
                            <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 font-bold">BALANCE DUE</span>
                            <div className="text-xs text-red-600 mt-1 font-medium">{formatCurrency(balance)}</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/orders/${order.id}`}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          View
                        </Link>
                        {order.shipping_method !== 'will_call' && (
                          <div className="flex items-center gap-1" title="Enter the actual booked carrier cost — recomputes dealer freight, order total & invoice">
                            <span className="text-xs text-gray-400">Actual&nbsp;$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={freightInput[order.id] ?? ''}
                              onChange={(e) => setFreightInput(prev => ({ ...prev, [order.id]: e.target.value }))}
                              placeholder="cost"
                              className="w-20 border rounded px-1 py-0.5 text-xs"
                            />
                            <label className="flex items-center gap-0.5 text-xs text-gray-500 cursor-pointer" title="Charge the dealer exactly this amount — skip the markup">
                              <input
                                type="checkbox"
                                checked={!!noMarkupInput[order.id]}
                                onChange={(e) => setNoMarkupInput(prev => ({ ...prev, [order.id]: e.target.checked }))}
                                className="cursor-pointer"
                              />
                              at&nbsp;cost
                            </label>
                            <button
                              onClick={() => handleSetActualFreight(order.id)}
                              disabled={actionLoading === order.id || !freightInput[order.id]}
                              className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              Set
                            </button>
                          </div>
                        )}
                        {order.shipping_method !== 'will_call' && (
                          <div className="flex items-center gap-1" title="Record our actual carrier cost (COGS) only — does NOT change the dealer charge or invoice. Use when the carrier billed a different amount than the dealer paid.">
                            <span className="text-xs text-gray-400">Cost&nbsp;$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={costInput[order.id] ?? ''}
                              onChange={(e) => setCostInput(prev => ({ ...prev, [order.id]: e.target.value }))}
                              placeholder="COGS"
                              className="w-20 border rounded px-1 py-0.5 text-xs"
                            />
                            <button
                              onClick={() => handleSetFreightCost(order.id)}
                              disabled={actionLoading === order.id || !costInput[order.id]}
                              className="px-2 py-0.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 disabled:opacity-50"
                              title="Records freight cost only — dealer is not re-charged"
                            >
                              Set Cost
                            </button>
                          </div>
                        )}
                        {activeTab === 'pending_freight' && !order.freight_paid && (
                          <button
                            onClick={() => handlePayFreight(order.id)}
                            disabled={actionLoading === order.id}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {actionLoading === order.id ? '...' : 'Pay Freight'}
                          </button>
                        )}
                        {activeTab === 'ready_to_ship' && (
                          <button
                            onClick={() => handleMarkShipped(order.id)}
                            disabled={actionLoading === order.id}
                            title="Mark shipped + resync FreightView tracking/PRO/BOL"
                            className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {actionLoading === order.id ? '...' : 'Mark Shipped'}
                          </button>
                        )}
                        {balanceOf(order) > 0.5 && (
                          parseFloat(String(order.pending_ach_amount || 0)) > 0.5 ? (
                            <span
                              className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 font-medium"
                              title="An ACH payment is in-flight (clearing). No reminder needed — don't nag."
                            >
                              ACH Pending
                            </span>
                          ) : (
                          <div className="flex items-center gap-1" title="Email the dealer a friendly balance-due reminder so we can book shipping. Tick 'urgent' to add the 'pay today to make this week's pickup' language.">
                            <label className="flex items-center gap-0.5 text-xs text-gray-500 cursor-pointer" title="Adds 'pay today to make this week's freight pickup' language">
                              <input
                                type="checkbox"
                                checked={!!urgentInput[order.id]}
                                onChange={(e) => setUrgentInput(prev => ({ ...prev, [order.id]: e.target.checked }))}
                                className="cursor-pointer"
                              />
                              urgent
                            </label>
                            <button
                              onClick={() => handleSendReminder(order.id)}
                              disabled={actionLoading === order.id}
                              className={clsx(
                                'px-3 py-1 text-white text-xs rounded disabled:opacity-50',
                                urgentInput[order.id] ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'
                              )}
                            >
                              {actionLoading === order.id ? '...' : remindedIds[order.id] ? 'Reminder Sent ✓' : 'Send Reminder'}
                            </button>
                          </div>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
